import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spin, Empty, Tag, Button, Alert, Tooltip } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import axiosInstance from '../../variables/api/axiosInstance';
import { formatDate, getUser, can, HAB } from '../../variables/constants';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { libelleDelai } from '../../i18n/donnees';

/**
 * « Ma journée » - la liste de ce que l'agent connecté doit faire, dans l'ordre.
 *
 * <h3>Pourquoi cet écran existe</h3>
 * Le reste de l'application expose le MODÈLE : 27 statuts, 41 entrées de menu,
 * 8 filtres sur la liste des sinistres. Pour répondre à « qu'est-ce que je fais
 * aujourd'hui ? », un rédacteur devait traduire son intention en vocabulaire
 * système - choisir le bon statut parmi 27, le trouver dans un tiroir de cartes,
 * puis retrouver le bon onglet parmi seize.
 *
 * Ici, rien à traduire : on descend une liste. Chaque ligne est un dossier et un
 * geste, cliquable jusqu'à l'endroit exact où le geste se fait.
 *
 * <h3>Aucun endpoint nouveau</h3>
 * Tout existait déjà et n'était simplement jamais rassemblé :
 *   - `/v1/delais/mes-alertes`      → mes échéances, avec le nombre de jours restants
 *   - `/v1/sinistres?redacteur=moi` → mes dossiers
 *   - `/v1/discussions/mes-mentions`→ les fils où un collègue m'a nommé
 *   - `/v1/mobile-backoffice/declarations` → la file mobile, si j'y ai droit
 *
 * Les quatre appels sont indépendants (`allSettled`) : la panne de l'un ne doit pas
 * vider l'écran des trois autres. Une section qui échoue le dit, les autres restent.
 */

const ACCENT = {
  retard: { couleur: '#dc2626', fond: '#fef2f2', bord: '#fecaca' },
  proche: { couleur: '#ea580c', fond: '#fff7ed', bord: '#fed7aa' },
  calme: { couleur: '#1a6b3c', fond: '#f0fdf4', bord: '#bbf7d0' },
};

const MUTED = '#6b7280';

/** Formule le reste à courir en français lisible, jamais en nombre nu. */
const resteEnClair = (jours) => {
  if (jours == null) return null;
  if (jours < 0) return i18n.t('dashboard:journee.reste.retard', { count: Math.abs(jours) });
  if (jours === 0) return i18n.t('dashboard:journee.reste.aujourdhui');
  if (jours === 1) return i18n.t('dashboard:journee.reste.demain');
  return i18n.t('dashboard:journee.reste.dans', { jours });
};

function Section({ titre, sous, items, vide, rendu, accent = 'calme', action }) {
  const a = ACCENT[accent];
  return (
    <section style={{ marginBottom: 28 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, color: '#101828', margin: 0 }}>
          {titre}
          {items.length > 0 && (
            <span style={{
              marginLeft: 8, fontSize: 12.5, fontWeight: 700, color: a.couleur,
              background: a.fond, border: `1px solid ${a.bord}`,
              borderRadius: 20, padding: '1px 9px',
            }}>
              {items.length}
            </span>
          )}
        </h2>
        {sous && <span style={{ fontSize: 12.5, color: MUTED }}>{sous}</span>}
        {action && <span style={{ marginLeft: 'auto' }}>{action}</span>}
      </div>

      {items.length === 0 ? (
        <div style={{
          padding: '16px 18px', background: '#fff', border: '1px solid #eceff3',
          borderRadius: 12, color: MUTED, fontSize: 13,
        }}>
          {vide}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {items.map(rendu)}
        </div>
      )}
    </section>
  );
}

/** Une ligne cliquable : le dossier à gauche, le geste attendu à droite. */
function Ligne({ titre, detail, droite, onClick, accent }) {
  const a = ACCENT[accent] || ACCENT.calme;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
      style={{
        display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
        padding: '12px 16px', background: '#fff',
        border: '1px solid #eceff3', borderLeft: `3px solid ${a.couleur}`,
        borderRadius: 10, cursor: 'pointer',
        transition: 'box-shadow 140ms ease',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 2px 10px rgba(16,24,40,0.07)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
    >
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 13.5, fontWeight: 650, color: '#101828' }}>{titre}</div>
        <div style={{
          fontSize: 12.5, color: MUTED, marginTop: 2,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {detail}
        </div>
      </div>
      {droite}
    </div>
  );
}

export default function MaJournee() {
  const { t } = useTranslation('dashboard');
  const navigate = useNavigate();
  const moi = getUser();

  const [chargement, setChargement] = useState(true);
  const [alertes, setAlertes] = useState([]);
  const [mesDossiers, setMesDossiers] = useState(null);
  const [mentions, setMentions] = useState([]);
  const [declarationsMobiles, setDeclarationsMobiles] = useState(null);
  const [pannes, setPannes] = useState([]);

  const peutVoirMobile = can(HAB.MOBILE_DECLARATION_READ);
  const peutVoirDiscussions = can(HAB.DISCUSSION_READ);

  const charger = useCallback(async () => {
    setChargement(true);
    setPannes([]);

    // skipErrorToast partout : cet ecran agrege quatre sources, et un bandeau rouge
    // par source en panne transformerait la page d'accueil en mur d'erreurs. Ce qui
    // echoue est dit une fois, en clair, dans `pannes`.
    const opts = { skipErrorToast: true };
    const [resAlertes, resDossiers, resMentions, resMobile] = await Promise.allSettled([
      axiosInstance.get('/v1/delais/mes-alertes', opts),
      moi?.trackingId
        ? axiosInstance.get('/v1/sinistres', { params: { page: 0, size: 1, redacteur: moi.trackingId }, ...opts })
        : Promise.reject(new Error('sans trackingId')),
      peutVoirDiscussions
        ? axiosInstance.get('/v1/discussions/mes-mentions', { params: { page: 0, size: 8 }, ...opts })
        : Promise.resolve(null),
      peutVoirMobile
        ? axiosInstance.get('/v1/mobile-backoffice/declarations', { params: { page: 0, size: 1 }, ...opts })
        : Promise.resolve(null),
    ]);

    const echecs = [];

    if (resAlertes.status === 'fulfilled') setAlertes(resAlertes.value.data?.data || []);
    else { setAlertes([]); echecs.push('echeances'); }

    if (resDossiers.status === 'fulfilled') setMesDossiers(resDossiers.value.data?.totalElements ?? 0);
    else setMesDossiers(null);

    if (resMentions.status === 'fulfilled') setMentions(resMentions.value?.data?.data || []);
    else { setMentions([]); echecs.push('mentions'); }

    if (resMobile.status === 'fulfilled') setDeclarationsMobiles(resMobile.value?.data?.totalElements ?? null);
    else setDeclarationsMobiles(null);

    setPannes(echecs);
    setChargement(false);
  }, [moi?.trackingId, peutVoirDiscussions, peutVoirMobile]);

  useEffect(() => { charger(); }, [charger]);

  /* Une echeance depassee et une echeance a venir ne se traitent pas le meme jour :
     elles sont separees, pas triees dans une meme liste ou le retard se noierait. */
  const enRetard = useMemo(
    () => alertes.filter((a) => (a.joursRestants ?? 0) < 0)
      .sort((a, b) => (a.joursRestants ?? 0) - (b.joursRestants ?? 0)),
    [alertes],
  );

  const cetteSemaine = useMemo(
    () => alertes.filter((a) => (a.joursRestants ?? 99) >= 0 && (a.joursRestants ?? 99) <= 7)
      .sort((a, b) => (a.joursRestants ?? 0) - (b.joursRestants ?? 0)),
    [alertes],
  );

  const plusTard = alertes.length - enRetard.length - cetteSemaine.length;
  const mentionsNonLues = mentions.filter((m) => !m.lu);

  const salutation = useMemo(() => {
    const h = dayjs().hour();
    return h < 13 ? 'bonjour' : h < 18 ? 'bonApresMidi' : 'bonsoir';
  }, []);

  if (chargement) {
    return <div style={{ textAlign: 'center', padding: 64 }}><Spin size="large" /></div>;
  }

  const ligneAlerte = (accent) => (a) => (
    <Ligne
      key={a.id}
      accent={accent}
      titre={a.sinistreNumeroLocal || t('journee.alerte.sansNumero')}
      detail={[libelleDelai(a), a.victimeNomPrenom, a.homologueRaisonSociale]
        .filter(Boolean).join(' · ')}
      droite={
        <Tooltip title={a.dateEcheance ? t('journee.alerte.echeance', { date: formatDate(a.dateEcheance) }) : t('journee.alerte.nonDatee')}>
          <Tag
            style={{
              borderRadius: 12, marginInlineEnd: 0, fontWeight: 650,
              color: ACCENT[accent].couleur,
              background: ACCENT[accent].fond,
              borderColor: ACCENT[accent].bord,
            }}
          >
            {resteEnClair(a.joursRestants)}
          </Tag>
        </Tooltip>
      }
      onClick={() => navigate(`/sinistres/${a.sinistreTrackingId}?tab=delais`)}
    />
  );

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#101828', margin: 0 }}>
          {t(`journee.salutation.${salutation}`)}{moi?.prenom ? ` ${moi.prenom}` : ''}
        </h1>
        <p style={{ color: MUTED, fontSize: 13.5, margin: '4px 0 0' }}>
          {dayjs().format('dddd D MMMM YYYY')}
          {mesDossiers != null && ` ${t('journee.dossiersConfies', { count: mesDossiers })}`}
        </p>
      </div>

      {pannes.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 20, borderRadius: 10 }}
          message={t('journee.pannes.message', { sources: pannes.map((p) => t(`journee.pannes.${p}`)).join(t('journee.pannes.separateur')) })}
          action={<Button size="small" icon={<ReloadOutlined />} onClick={charger}>{t('journee.pannes.reessayer')}</Button>}
        />
      )}

      <Section
        titre={t('journee.enRetard.titre')}
        sous={t('journee.enRetard.sousTitre')}
        accent="retard"
        items={enRetard}
        vide={t('journee.enRetard.vide')}
        rendu={ligneAlerte('retard')}
      />

      <Section
        titre={t('journee.cetteSemaine.titre')}
        sous={t('journee.cetteSemaine.sousTitre')}
        accent="proche"
        items={cetteSemaine}
        vide={t('journee.cetteSemaine.vide')}
        rendu={ligneAlerte('proche')}
        action={plusTard > 0 ? (
          <span style={{ fontSize: 12.5, color: MUTED }}>
            {t('journee.cetteSemaine.plusLointaines', { count: plusTard })}
          </span>
        ) : null}
      />

      {peutVoirDiscussions && (
        <Section
          titre={t('journee.mentions.titre')}
          sous={t('journee.mentions.sousTitre')}
          items={mentionsNonLues}
          vide={t('journee.mentions.vide')}
          rendu={(m) => (
            <Ligne
              key={m.messageTrackingId}
              titre={m.entiteLibelle || t('journee.mentions.discussion')}
              detail={`${m.auteur?.nomComplet || m.auteur?.login || t('journee.mentions.unCollegue')} - ${(m.corps || '').slice(0, 110)}`}
              droite={
                <span style={{ fontSize: 12, color: MUTED }}>
                  {m.date ? formatDate(m.date) : ''}
                </span>
              }
              onClick={() => navigate(`/sinistres/${m.entiteTrackingId}?tab=discussion`)}
            />
          )}
        />
      )}

      {/* Raccourcis : seulement ce qui a du contenu. Une tuile vide vers une file
          vide devient une tuile qu'on cesse de regarder. */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {peutVoirMobile && declarationsMobiles > 0 && (
          <Button
            size="large"
            onClick={() => navigate('/mobile/declarations')}
            style={{ borderRadius: 10, borderColor: '#f0abfc', color: '#a21caf', fontWeight: 600 }}
          >
            {t('journee.declarationsMobiles', { count: declarationsMobiles })}
          </Button>
        )}
        <Button
          size="large"
          onClick={() => navigate(moi?.trackingId ? '/sinistres' : '/sinistres')}
          style={{ borderRadius: 10 }}
        >
          {t('journee.ouvrirMesDossiers')}
        </Button>
        <Button
          size="large"
          onClick={() => navigate('/delais')}
          style={{ borderRadius: 10 }}
        >
          {t('journee.tousLesDelais')}
        </Button>
      </div>

      {alertes.length === 0 && mentionsNonLues.length === 0 && (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t('journee.rienAFaire')}
          style={{ marginTop: 32 }}
        />
      )}
    </div>
  );
}
